import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { Editor } from '@tinymce/tinymce-react';
import { FiArrowLeft, FiMail, FiSend, FiFileText, FiEdit2, FiClock, FiType, FiAlignLeft, FiImage, FiChevronsDown, FiMinus, FiPlay, FiCheck } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { CampaignBuilderInfo } from '../../../model/GetCampaignsResult';
import api from '../../../api/api';
import { toast } from '../Toast';
import { to, fillPreviewLine } from '../../../util/util';
import { uploadEditorImage } from '../../campaign/uploadEditorImage';
import { SendTestModal, SendCampaignModal } from './CampaignModals';
const autosaveMs = 5000;

const blockMime = 'application/x-investi-block';
type Block = { key: string; label: string; icon: React.ReactNode; html?: string };
const blocks: Block[] = [
    { key: 'heading', label: 'Heading', icon: <FiType />, html: '<h2>Your heading</h2>' },
    { key: 'text', label: 'Text', icon: <FiAlignLeft />, html: '<p>Write your text here.</p>' },
    { key: 'picture', label: 'Picture', icon: <FiImage /> },
    { key: 'button', label: 'Button', icon: <FiSend />, html: '<p><a href="#" style="display:inline-block;padding:12px 24px;background:#00B5D1;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:600">Button text</a></p>' },
    { key: 'spacer', label: 'Spacer', icon: <FiChevronsDown />, html: '<div style="height:24px;line-height:24px;font-size:1px">&nbsp;</div>' },
    { key: 'divider', label: 'Divider', icon: <FiMinus />, html: '<hr style="border:0;border-top:1px solid #e5e9f0;margin:24px 0">' },
    { key: 'video', label: 'Video', icon: <FiPlay /> },
];

// the template rendered twice and clipped above / below {{content_placeholder}}, so the editor sits between its header and footer
const TemplatePart: React.FC<{ html: string; part: 'top' | 'bottom' }> = ({ html, part }) => {
    const [height, setHeight] = useState(0);
    const fit = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
        const doc = e.currentTarget.contentDocument;
        const marker = doc?.getElementById('investi-marker');
        if (!doc || !marker) return;
        const top = marker.getBoundingClientRect().top;
        const total = doc.documentElement.scrollHeight;
        setHeight(part === 'top' ? top : total - top);
        if (part === 'bottom') doc.defaultView?.scrollTo(0, top);
    };
    return <iframe title={`Template ${part}`} className="template-part" scrolling="no" sandbox="allow-same-origin" style={{ height }} onLoad={fit}
        srcDoc={html.replace('{{content_placeholder}}', '<div id="investi-marker"></div>')} />;
};

// spec 2:59658. TinyMCE body inside the template (not a block builder); Smart / Design / Settings tabs, approval and scheduling omitted.
const EmailBuilder: React.FC<{ campaignId: string }> = ({ campaignId }) => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const user = useSelector((state: IGlobalState) => state.user);
    const editorRef = useRef<any>(null);
    const [device, setDevice] = useState<'Computer' | 'Phone'>('Computer');
    const [info, setInfo] = useState<CampaignBuilderInfo | null>(null);
    const [error, setError] = useState('');
    const [body, setBody] = useState('');
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState<Date | null>(null);
    const savedBody = useRef('');
    const latestBody = useRef('');
    const timer = useRef<number>();
    const [testSent, setTestSent] = useState(false);
    const [modal, setModal] = useState<'test' | 'send' | null>(null);

    useAsyncEffect(async isMounted => {
        const [err, data] = await to(api().getCampaignBuilder(campaignId));
        if (!isMounted()) return;
        if (err || !data) { setError(err?.message ?? 'Loading failed'); return; }
        const i: CampaignBuilderInfo = data;
        // the builder only edits drafts; the list links non-drafts to /details directly, this covers old links
        if (i.status !== 'Draft') { history.replace(`/communications/campaign/${encodeURIComponent(campaignId)}/details`); return; }
        if (!i.templateHtml) { setError('The template this campaign was built on has been deleted.'); return; }
        setInfo(i);
        setBody(i.editorContent);
        setTestSent(i.testSent);
        savedBody.current = i.editorContent;
        latestBody.current = i.editorContent;
    }, [campaignId]);


    const save = async () => {
        window.clearTimeout(timer.current);
        if (!info) return;
        // on unmount the editor may already be torn down; fall back to the last change it reported
        const editor = editorRef.current?.removed ? null : editorRef.current;
        if (editor) await editor.uploadImages();
        const html: string = editor ? editor.getContent() : latestBody.current;
        if (html === savedBody.current) return;
        setSaving(true);
        const result = await api().saveCampaign({
            stockId: stock.id, campaignId, templateId: info.templateId, subject: info.subject, editorContent: html,
            html: fillPreviewLine(info.templateHtml!.replace('{{content_placeholder}}', () => html), info.previewLine),
            listId: info.listId, previewLine: info.previewLine,
        }).catch(e => ({ errorMessage: e.message as string }));
        setSaving(false);
        if (result.errorMessage) { toast.error(`Autosave failed: ${result.errorMessage}`); return false; }
        savedBody.current = html;
        setSavedAt(new Date());
        return true;
    };
    // the timer and unmount call the latest save, not the one from the render that scheduled them
    const saveRef = useRef(save);
    saveRef.current = save;

    const onEditorChange = (html: string) => {
        latestBody.current = html;
        setBody(html);
        // the server clears Campaign.TestSent when the saved body changes
        if (html !== savedBody.current) setTestSent(false);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => saveRef.current(), autosaveMs);
    };

    useEffect(() => {
        const warn = (e: BeforeUnloadEvent) => { if (editorRef.current && editorRef.current.getContent() !== savedBody.current) e.preventDefault(); };
        window.addEventListener('beforeunload', warn);
        return () => { window.removeEventListener('beforeunload', warn); saveRef.current(); };
    }, []);

    const sendTest = async (testEmailAddresses: string, addTestToSubject: boolean) => {
        const saved = await save();
        if (saved === false) return false;
        const result = await api().sendCampaign({ stockId: stock.id, campaignId, isPreview: true, testEmailAddresses, addTestToSubject })
            .catch(e => ({ success: false, errorMessage: e.message as string, previewSentTo: '' }));
        if (!result.success) { toast.error(`Test failed: ${result.errorMessage}`); return false; }
        setTestSent(true);
        toast.success(`Test sent to ${result.previewSentTo}`);
        return true;
    };

    const send = async () => {
        const saved = await save();
        if (saved === false) return false;
        const result = await api().sendCampaign({ stockId: stock.id, campaignId, isPreview: false, listId: info!.listId })
            .catch(e => ({ success: false, errorMessage: e.message as string, previewSentTo: '' }));
        if (!result.success) { toast.error(`Send failed: ${result.errorMessage}`); return false; }
        toast.success('Campaign is sending');
        history.push('/communications/email-campaigns');
        return true;
    };

    const insertBlock = (key: string) => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.focus();
        if (key === 'picture') { editor.execCommand('mceImage'); return; }
        if (key === 'video') {
            const id = window.prompt('YouTube link')?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)?.[1];
            if (id) editor.insertContent(`<p><a href="https://www.youtube.com/watch?v=${id}" target="_blank"><img src="https://img.youtube.com/vi/${id}/sddefault.jpg" alt="YouTube video" style="max-width:100%"></a></p>`);
            return;
        }
        editor.insertContent(blocks.find(b => b.key === key)?.html ?? '');
    };

    const back = <button type="button" className="link back" onClick={() => history.push('/communications/email-campaigns')}><FiArrowLeft /> Back to Email campaigns</button>;
    if (error) return <div className="announcement-preview email-builder">{back}<p className="error">{error}</p></div>;
    if (!info || !stock) return <div className="announcement-preview email-builder">{back}<p className="loading">Loading campaign…</p></div>;
    const templateHtml = fillPreviewLine(info.templateHtml!, info.previewLine);
    const hasBody = /<img/.test(body) || body.replace(/<[^>]*>|&nbsp;/g, '').trim() !== '';
    const checks: [string, boolean][] = [
        ['Subject line added', info.subject.trim() !== ''],
        [`Recipients chosen (${info.recipients.toLocaleString()})`, info.recipients > 0],
        ['Body has content', hasBody],
        ['Unsubscribe link present', templateHtml.includes('{{list_unsubscribe}}')],
        ['Test sent and checked', testSent],
    ];
    const ready = checks.every(([, ok]) => ok);
    const savedText = saving ? 'Saving…' : savedAt ? `Saved at ${moment(savedAt).format('h:mm a')}` : 'Saves automatically as you type';

    return <div className="announcement-preview email-builder">
        {back}
        <div className="title-row">
            <div>
                <h1>{info.subject}</h1>
                <div className="meta"><span className="pill warn">{info.status}</span><span>{savedText}</span></div>
            </div>
            <div className="actions">
                <button type="button" className="btn-outline" onClick={() => setModal('test')}><FiMail /> Send a Test</button>
                <button type="button" className="btn-primary" onClick={() => setModal('send')} disabled={!ready}><FiSend /> Send</button>
            </div>
        </div>
        {modal === 'test' && <SendTestModal defaultTo={user.username} onClose={() => setModal(null)} onSend={sendTest} />}
        {modal === 'send' && <SendCampaignModal subject={info.subject} recipients={info.recipients} onClose={() => setModal(null)} onSend={send} />}

        <div className="builder">
            <div className="card side-nav">
                <div className="label">This campaign</div>
                <button type="button" className="nav-item" disabled title="Coming soon">
                    <span className="icon"><FiFileText /></span><span className="t"><strong>Campaign Details</strong><span>What it is and who gets it</span></span>
                </button>
                <button type="button" className="nav-item active">
                    <span className="icon"><FiEdit2 /></span><span className="t"><strong>Email Builder</strong><span>Write and design it</span></span>
                </button>
                <div className="email-status">
                    <div className="label">Status</div>
                    <div className="status warn"><FiClock /> Draft — not sent</div>
                    <div className="saved">{savedText}</div>
                </div>
            </div>

            <div className="canvas">
                <div className="device">
                    {(['Computer', 'Phone'] as const).map(d => <button key={d} type="button" className={d === device ? 'active' : ''} onClick={() => setDevice(d)}>{d}</button>)}
                </div>
                <div className={`frame ${device.toLowerCase()}`} key={device}>
                    <TemplatePart html={templateHtml} part="top" />
                    <Editor
                        onInit={(_: any, editor: any) => { editorRef.current = editor; }}
                        apiKey="fntw2t4q38u7usw5dk4ew34dbgz96zhpezwidg8edbp5jv1k"
                        value={body}
                        onEditorChange={onEditorChange}
                        init={{
                            onboarding: false,
                            menubar: false,
                            statusbar: false,
                            plugins: ['autoresize', 'lists', 'link', 'image', 'code'],
                            min_height: 240,
                            autoresize_bottom_margin: 16,
                            toolbar: 'blocks | bold italic underline forecolor | alignleft aligncenter alignright | bullist numlist | link image | removeformat code',
                            toolbar_mode: 'sliding',
                            content_style: 'body { font-family:Arial,sans-serif; font-size:15px; color:#334155; margin:12px 20px } img[data-uploading]{opacity:.35}',
                            valid_elements: '*[*]',
                            extended_valid_elements: '*[*]',
                            verify_html: false,
                            convert_urls: false,
                            automatic_uploads: true,
                            paste_data_images: true,
                            images_upload_handler: uploadEditorImage(stock.id, () => editorRef.current),
                            setup: (editor: any) => {
                                editor.on('dragover', (e: DragEvent) => { if (e.dataTransfer?.types.includes(blockMime)) e.preventDefault(); });
                                editor.on('drop', (e: DragEvent) => {
                                    const key = e.dataTransfer?.getData(blockMime);
                                    if (!key) return;
                                    e.preventDefault();
                                    editor.selection.placeCaretAt(e.clientX, e.clientY);
                                    insertBlock(key);
                                });
                            },
                        }}
                    />
                    <TemplatePart html={templateHtml} part="bottom" />
                </div>
            </div>

            <div className="right">
                <div className="card blocks">
                    <h2>Blocks</h2>
                    <p className="hint">Drag a block into the email, or click it to add it where your cursor is.</p>
                    <div className="tiles">
                        {blocks.map(b => <button key={b.key} type="button" className="tile" draggable
                            onDragStart={e => e.dataTransfer.setData(blockMime, b.key)} onClick={() => insertBlock(b.key)}>
                            <span className="icon">{b.icon}</span>{b.label}
                        </button>)}
                    </div>
                </div>
                <div className="card checklist">
                    <h2>Before You Send</h2>
                    <ul>{checks.map(([text, ok]) => <li key={text} className={ok ? 'ok' : ''}><span className="tick">{ok && <FiCheck />}</span>{text}</li>)}</ul>
                    <p className="hint">“Send” unlocks when every item is ticked.</p>
                </div>
            </div>
        </div>
    </div>;
};

export default EmailBuilder;
